import {
  CellContext,
  ColumnDef,
  createColumnHelper,
} from "@tanstack/react-table";
import Table from "../../../ui/Table/Table";
import Select from "../../../ui/Select/Select";
import toSentenceCase from "../../../util/toSentenceCase";
import styles from "./FieldsTable.module.scss";
import { Controller, useFormContext } from "react-hook-form";
import { z } from "zod";
import Feedback from "../../../ui/Feedback/Feedback";
import TextInput from "../../../ui/TextInput/TextInput";
import DatePickerInput from "../../../ui/DatePickerInput/DatePickerInput";
import NumberInput from "../../../ui/NumberInput/NumberInput";
import { useCallback, useMemo, useState } from "react";
import IconButton from "../../../ui/IconButton/IconButton";
import { DotsVertical, Refresh } from "../../../ui/Icon/Icon";
import Dropdown from "../../../ui/Dropdown/Dropdown";
import { formatISO } from "date-fns";
import { SupportedMacawField } from "../../../__generated__/gql/graphql";
import webflowFieldTypeSchema from "./webflow-field-type-schema";

type SupportedFields =
  (typeof SupportedMacawField)[keyof typeof SupportedMacawField];

const supportedFieldLabels: Record<SupportedMacawField, string> = {
  title: "Title",
  body: "Body (HTML)",
  excerpt: "Excerpt",
  metaDescription: "Meta description",
  heroImage: "Hero image",
  tag: "Tag",
  createdAt: "Created at",
  updatedAt: "Updated at",
  generatedAt: "Generated at",
} as const;

const supportedFieldsByFieldType: Record<
  WebflowFieldType,
  SupportedMacawField[]
> = {
  PlainText: [
    SupportedMacawField.Title,
    SupportedMacawField.Excerpt,
    SupportedMacawField.MetaDescription,
  ],
  RichText: [SupportedMacawField.Body],
  MultiReference: [SupportedMacawField.Tag],
  Reference: [SupportedMacawField.Tag],
  File: [SupportedMacawField.HeroImage],
  Option: [SupportedMacawField.Tag],
  Color: [],
  Switch: [],
  DateTime: [
    SupportedMacawField.CreatedAt,
    SupportedMacawField.GeneratedAt,
    SupportedMacawField.UpdatedAt,
  ],
  Number: [],
  Phone: [],
  Email: [],
  Link: [],
  VideoLink: [],
  Image: [SupportedMacawField.HeroImage],
  MultiImage: [SupportedMacawField.HeroImage],
};

export type FieldRow = {
  webflowField: {
    id: string;
    name: string;
    type: WebflowFieldType;
    displayName: string;
    isRequired: boolean;
    isEditable: boolean;
    options?: { value: string; label: string }[] | null;
  };
  defaultValue?: string | null;
  macawField?: SupportedFields | null;
};

type WebflowFieldType = z.infer<typeof webflowFieldTypeSchema>;

function FieldTypeFactory({
  type,
  onChange,
  value,
  options,
}: {
  type: WebflowFieldType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (...event: any[]) => void;
  value?: string;
  options?: { value: string; label: string }[];
}) {
  if (["PlainText", "RichText"].includes(type)) {
    return (
      <TextInput className={styles.input} onChange={onChange} value={value} />
    );
  }

  if (type === "Reference") {
    return (
      <TextInput
        className={styles.input}
        placeholder="63764ec7981aa0138e99abc4"
        onChange={onChange}
        value={value}
      />
    );
  }

  if (type === "MultiReference") {
    return (
      <TextInput
        className={styles.input}
        placeholder="63764ec7981aa0138e99abc4, 63764ec7981aa0138e99abc4"
        onChange={onChange}
        value={value}
      />
    );
  }

  if (type === "File") {
    return (
      <TextInput
        className={styles.input}
        placeholder="https://website.com/rows.csv"
        onChange={onChange}
        value={value}
      />
    );
  }

  if (type === "Option") {
    return (
      <Select
        className={styles.input}
        placeholder="Select an option"
        withinPortal
        clearable
        data={options ?? []}
        onChange={onChange}
        value={value}
      />
    );
  }

  if (type === "Color") {
    return (
      <TextInput
        className={styles.input}
        placeholder="#000000"
        onChange={onChange}
        value={value}
      />
    );
  }

  if (type === "Switch") {
    return (
      <Select
        className={styles.input}
        withinPortal
        placeholder="False"
        clearable
        data={[
          { label: "True", value: "TRUE" },
          { label: "False", value: "FALSE" },
        ]}
        onChange={onChange}
        value={value}
      />
    );
  }

  if (type === "DateTime") {
    return (
      <DatePickerInput
        className={styles.input}
        placeholder="Jan 01, 2024"
        onChange={(e) =>
          onChange(
            e
              ? `${formatISO(e, {
                  representation: "date",
                })}T00:00:00Z`
              : undefined,
          )
        }
        value={value ? new Date(value) : undefined}
      />
    );
  }

  if (type === "Number") {
    return (
      <NumberInput
        className={styles.input}
        placeholder="1234"
        onChange={onChange}
        value={value}
      />
    );
  }

  if (type === "Phone") {
    return (
      <TextInput
        className={styles.input}
        placeholder="2024561111"
        onChange={onChange}
        value={value}
      />
    );
  }

  if (type === "Email") {
    return (
      <TextInput
        className={styles.input}
        placeholder="email@example.com"
        onChange={onChange}
        value={value}
      />
    );
  }

  if (type === "Link") {
    return (
      <TextInput
        className={styles.input}
        placeholder="https://website.com"
        onChange={onChange}
        value={value}
      />
    );
  }

  if (type === "VideoLink") {
    return (
      <TextInput
        className={styles.input}
        placeholder="https://youtube.com/watch?v=video_id"
        onChange={onChange}
        value={value}
      />
    );
  }

  if (type === "Image") {
    return (
      <TextInput
        className={styles.input}
        placeholder="https://website.com/image.jpg"
        onChange={onChange}
        value={value}
      />
    );
  }

  if (type === "MultiImage") {
    return (
      <TextInput
        className={styles.input}
        placeholder="https://website.com/image-1.jpg, https://website.com/image-2.jpg"
        onChange={onChange}
        value={value}
      />
    );
  }
}

const WebflowFieldCell = ({
  cell,
}: CellContext<FieldRow, FieldRow["webflowField"]>) => {
  return (
    <span className={styles.webflowField}>
      {toSentenceCase(cell.getValue().displayName)}
      {cell.getValue().isRequired && (
        <span className={styles.webflowFieldRequired}>· Required</span>
      )}
    </span>
  );
};

const MacawFieldCell = ({
  cell,
}: CellContext<FieldRow, FieldRow["macawField"]>) => {
  const { control, getFieldState, getValues, setValue } =
    useFormContext<FieldsTableFormData>();
  const fields = getValues(`fields`);
  const index = fields.findIndex(
    (field) => field.webflowField.name === cell.row.original.webflowField.name,
  );
  const { error } = getFieldState(`fields.${index}`);
  const webflowField = getValues(`fields.${index}.webflowField`);
  const [isMappedField, setIsMappedField] = useState(
    supportedFieldsByFieldType[webflowField.type].length > 0,
  );

  const toggleIsMapped = useCallback(
    (value: boolean) => {
      setIsMappedField(value);
      setValue(`fields.${index}.macawField`, null);
      setValue(`fields.${index}.defaultValue`, null);
    },
    [index, setValue],
  );

  if (isMappedField) {
    return (
      <Controller
        control={control}
        key={`fields.${index}.macawField`}
        name={`fields.${index}.macawField`}
        render={({ field: { onChange, value } }) => (
          <>
            <div className={styles.inputRow}>
              <Select
                className={styles.input}
                withinPortal
                value={value}
                onChange={onChange}
                clearable={!webflowField.isRequired}
                placeholder="Select field"
                data={supportedFieldsByFieldType[webflowField.type].map(
                  (value) => ({
                    value,
                    label: supportedFieldLabels[value],
                  }),
                )}
              />
              <Dropdown
                asChild
                items={[
                  {
                    items: [
                      {
                        IconComponent: Refresh,
                        label: "Change to manual entry",
                        onClick: () => toggleIsMapped(false),
                      },
                    ],
                  },
                ]}
              >
                <IconButton
                  aria-label="More options "
                  IconComponent={DotsVertical}
                />
              </Dropdown>
            </div>

            {error?.root?.message && (
              <Feedback className={styles.feedback}>
                {error.root.message}
              </Feedback>
            )}
          </>
        )}
      />
    );
  }

  return (
    <Controller
      key={`fields.${index}.defaultValue`}
      control={control}
      name={`fields.${index}.defaultValue`}
      render={({ field: { onChange, value } }) => (
        <>
          <div className={styles.inputRow}>
            <FieldTypeFactory
              options={webflowField.options ?? undefined}
              type={webflowField.type}
              onChange={onChange}
              value={value === null ? undefined : value}
            />

            <Dropdown
              asChild
              items={[
                {
                  items: [
                    {
                      IconComponent: Refresh,
                      label: "Change to mapped field",
                      tooltip:
                        supportedFieldsByFieldType[webflowField.type].length > 0
                          ? undefined
                          : "This field cannot be mapped to a field in Macaw",
                      disabled:
                        supportedFieldsByFieldType[webflowField.type].length ===
                        0,
                      onClick: () => toggleIsMapped(true),
                    },
                  ],
                },
              ]}
            >
              <IconButton
                aria-label="More options"
                IconComponent={DotsVertical}
              />
            </Dropdown>
          </div>
          {error?.root?.message && (
            <Feedback className={styles.feedback}>
              {error.root.message}
            </Feedback>
          )}
        </>
      )}
    />
  );
};

const columnHelper = createColumnHelper<FieldRow>();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columns: ColumnDef<FieldRow, any>[] = [
  columnHelper.accessor("webflowField", {
    header: "Webflow field",
    cell: WebflowFieldCell,
  }),
  columnHelper.accessor("macawField", {
    header: "Macaw field",
    cell: MacawFieldCell,
    size: 370,
  }),
];

export type FieldsTableFormData = {
  fields: FieldRow[];
};

function FieldsTable({
  data,
  loading,
}: {
  data: FieldRow[];
  loading?: boolean;
}) {
  const sortedData = useMemo(
    () => [...data].sort((a) => (a.webflowField.isRequired ? -1 : 1)),
    [data],
  );

  return (
    <Table
      loading={loading}
      data={sortedData}
      columns={columns}
      className={styles.table}
    />
  );
}

export default FieldsTable;
